import { useRetainedState } from "@appretio/shared";
import { type SetStateAction, useCallback, useMemo } from "react";
import type { MultiTableCellValueChanged } from "../../components/multiTableGrid/MultiTableGrid";
import type { EditConfig, GridData } from "../../utils/summary/editPropagation";
import {
  filterToPerTableRows,
  parseColumnId,
  renderCellSlices,
  renderColumnar
} from "../../utils/summary/gridDataTransforms";
import {
  POTB_METRICS,
  SALES_KPI_DERIVATIONS,
  STOCK_KPI_FLOWS
} from "../../utils/summary/salesKpiDerivations";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import type { ForecastSummaryData } from "../useFetchForecastSummaryData";
import { applyCellEdit, parseResponseData } from "./summaryGridHelpers";

type Columns = Record<string, (number | null)[][]>;

/** Labels and layers an edit needs to resolve its row and cell view. */
interface EditContext {
  filteredRowLabelsPerTable: string[][];
  unifiedRowLabels: string[];
  editConfig: EditConfig;
  serverColumns: Columns;
}

/** Resolves the edited row and applies the edit with its rendered cell view. */
function applyGridEdit(
  prev: GridData,
  event: MultiTableCellValueChanged,
  ctx: EditContext
): GridData {
  const labels = ctx.filteredRowLabelsPerTable[event.tableIndex] ?? [];
  const unifiedRowIndex = ctx.unifiedRowLabels.indexOf(labels[event.rowIndex]);
  if (unifiedRowIndex === -1) return prev;

  // The KPI cascade starts from the cell as rendered from `prev` — not from a
  // render snapshot — so batched edits (e.g. an AG Grid range paste, where
  // React batches the setGridData calls without re-rendering in between) see
  // each other's effects. Each edit cascades over its own slice: the ratios
  // the cascade holds fixed (AUR/AUC) differ per slice, so cascading a TTL
  // edit over PTG would drift the TTL ratios.
  const { agg } = parseColumnId(event.column);
  const slices = renderCellSlices(
    prev,
    event.tableIndex,
    unifiedRowIndex,
    ctx.editConfig,
    ctx.serverColumns
  );
  const view = {
    PTD: slices.ptdValues,
    PTG: slices.ptgValues,
    TTL: slices.ttlValues
  }[agg];
  return applyCellEdit(prev, event, unifiedRowIndex, ctx.editConfig, view);
}

/** Renders the columnar view and overlays the server-owned index columns. */
function renderColumnarData(
  gridData: GridData | null,
  editConfig: EditConfig,
  indexColumns: Columns,
  serverColumns: Columns,
  hasEditsSinceLoad: boolean
): Columns {
  if (!gridData) return {};
  const rectangular = renderColumnar(
    gridData,
    editConfig.irreducibleMetrics,
    editConfig.kpiFlows,
    editConfig.derivedMetrics,
    {
      endpointMetrics: editConfig.endpointMetrics,
      ptdMutable: editConfig.ptdMutable,
      serverColumns,
      invalidatedMetrics: hasEditsSinceLoad ? POTB_METRICS : []
    }
  );
  Object.assign(rectangular, indexColumns);
  return rectangular;
}

/** Inputs {@link useRenderedColumns} needs to render and filter the columns. */
interface RenderedColumnsParams {
  gridData: GridData | null;
  editConfig: EditConfig;
  indexColumns: Columns;
  serverColumns: Columns;
  /** Blanks the metrics an edit has made unanswerable (see POTB_METRICS). */
  hasEditsSinceLoad: boolean;
  tableLabels: string[];
  unifiedRowLabels: string[];
  realRowsPerTable: Set<string>[];
}

/** The rendered columnar view plus the per-table slice the grid renders. */
function useRenderedColumns({
  gridData,
  editConfig,
  indexColumns,
  serverColumns,
  hasEditsSinceLoad,
  tableLabels,
  unifiedRowLabels,
  realRowsPerTable
}: RenderedColumnsParams) {
  const columnarData = useMemo(
    () =>
      renderColumnarData(
        gridData,
        editConfig,
        indexColumns,
        serverColumns,
        hasEditsSinceLoad
      ),
    [gridData, editConfig, indexColumns, serverColumns, hasEditsSinceLoad]
  );

  const filtered = useMemo(() => {
    if (!gridData || tableLabels.length === 0) {
      return { filteredData: {}, filteredRowLabelsPerTable: [] as string[][] };
    }
    return filterToPerTableRows(
      columnarData,
      tableLabels,
      unifiedRowLabels,
      realRowsPerTable
    );
  }, [columnarData, tableLabels, unifiedRowLabels, realRowsPerTable, gridData]);

  return { columnarData, ...filtered };
}

/**
 * What one summary response loads besides the editable ``gridData``: one
 * value, so a response replaces it whole and a clear empties it whole.
 */
interface SummaryLoad {
  editConfig: EditConfig;
  tableLabels: string[];
  unifiedRowLabels: string[];
  realRowsPerTable: Set<string>[];
  indexColumns: Columns;
  /** The server-owned base layer: the padded payload as-is, nulls intact. */
  serverColumns: Columns;
  metrics: string[];
  editableAggs: string[];
  indexError: string | null;
}

const EMPTY_LOAD: SummaryLoad = {
  editConfig: {
    irreducibleMetrics: [],
    kpiFlows: STOCK_KPI_FLOWS,
    endpointMetrics: [],
    derivedMetrics: SALES_KPI_DERIVATIONS,
    ptdMutable: false
  },
  tableLabels: [],
  unifiedRowLabels: [],
  realRowsPerTable: [],
  indexColumns: {},
  serverColumns: {},
  metrics: [],
  editableAggs: [],
  indexError: null
};

/**
 * Whether the grid was edited since the load, and since the last save. A save
 * leaves POTB blank until the next load, so it clears only ``hasUnsavedEdits``.
 */
function useSummaryEditFlags(scope: string) {
  const [hasEditsSinceLoad, setHasEditsSinceLoad] = useRetainedState(
    scope,
    "hasEditsSinceLoad",
    false
  );
  const [hasUnsavedEdits, setHasUnsavedEdits] = useRetainedState(
    scope,
    "hasUnsavedEdits",
    false
  );
  const markEdited = useCallback(() => {
    setHasEditsSinceLoad(true);
    setHasUnsavedEdits(true);
  }, [setHasEditsSinceLoad, setHasUnsavedEdits]);
  const markPotbStale = useCallback(
    () => setHasEditsSinceLoad(true),
    [setHasEditsSinceLoad]
  );
  const resetEdits = useCallback(() => {
    setHasEditsSinceLoad(false);
    setHasUnsavedEdits(false);
  }, [setHasEditsSinceLoad, setHasUnsavedEdits]);
  return {
    hasEditsSinceLoad,
    hasUnsavedEdits,
    setHasUnsavedEdits,
    markEdited,
    markPotbStale,
    resetEdits
  };
}

/**
 * Manages grid data state, apply/edit handlers, and derived columnar/filtered
 * data. Retained per page, so navigating away and back restores the loaded
 * grid — unsaved cell edits included, since they live in ``gridData``.
 */
export function useSummaryGridData(page: CubePage) {
  const scope = pageScope(page);
  const [load, setLoad] = useRetainedState<SummaryLoad>(
    scope,
    "load",
    EMPTY_LOAD
  );
  const [gridData, setGridData] = useRetainedState<GridData | null>(
    scope,
    "gridData",
    null
  );
  const [savedAt, setSavedAt] = useRetainedState<string | null>(
    scope,
    "savedAt",
    null
  );
  const editFlags = useSummaryEditFlags(scope);
  const { hasEditsSinceLoad, markEdited, resetEdits } = editFlags;

  function applyResponse(response: ForecastSummaryData, markdownSign = 0) {
    const parsed = parseResponseData(response, markdownSign);
    setLoad({
      editConfig: parsed.editConfig,
      tableLabels: response.table_labels,
      unifiedRowLabels: parsed.unifiedRowLabels,
      realRowsPerTable: parsed.realRowsPerTable,
      indexColumns: parsed.idxData,
      serverColumns: parsed.paddedData,
      metrics: parsed.metrics,
      editableAggs: response.editable_aggs,
      indexError: response.index_error ?? null
    });
    resetEdits();
    return parsed;
  }

  // Back to the empty state, as before the first Apply.
  const clearLoadedData = useCallback(() => {
    setLoad(EMPTY_LOAD);
    setGridData(null);
    setSavedAt(null);
    resetEdits();
  }, [setLoad, setGridData, setSavedAt, resetEdits]);

  // The intake swap writes the opening stock into the server layer.
  const setServerColumns = useCallback(
    (update: SetStateAction<Columns>) =>
      setLoad((prev) => ({
        ...prev,
        serverColumns:
          typeof update === "function" ? update(prev.serverColumns) : update
      })),
    [setLoad]
  );

  const { columnarData, filteredData, filteredRowLabelsPerTable } =
    useRenderedColumns({ ...load, gridData, hasEditsSinceLoad });

  const { editConfig, unifiedRowLabels, serverColumns } = load;
  const handleCellValueChanged = useCallback(
    function handleCellValueChanged(event: MultiTableCellValueChanged) {
      if (!gridData) return;
      const ctx = {
        filteredRowLabelsPerTable,
        unifiedRowLabels,
        editConfig,
        serverColumns
      };
      setGridData((prev) => (prev ? applyGridEdit(prev, event, ctx) : prev));
      markEdited();
    },
    [
      gridData,
      editConfig,
      filteredRowLabelsPerTable,
      unifiedRowLabels,
      serverColumns,
      setGridData,
      markEdited
    ]
  );

  return {
    ...load,
    gridData,
    setGridData,
    setServerColumns,
    hasIndexData: Object.keys(load.indexColumns).length > 0,
    savedAt,
    setSavedAt,
    ...editFlags,
    filteredData,
    filteredRowLabelsPerTable,
    columnarData,
    applyResponse,
    clearLoadedData,
    handleCellValueChanged
  };
}
